import axios from 'axios';
import { store } from '@redux/store';
import {
  showErrorPopup,
  showLoading,
  hideLoading,
  showMaintain,
  clearPersistRoot
} from '@redux/actions';
import { initializeReducers } from 'initializable-reducer';
import { ResponseStatus } from '@constants/constant';
import { checkActiveSubscriptionPause } from '@utils';

// プロトコル部分の://以外で//を/に置換して修正する関数
const normalizeUrlSlashes = (rawUrl) => {
  if (typeof rawUrl !== 'string') {
    return rawUrl;
  }

  const protocolSeparatorIndex = rawUrl.indexOf('://');

  if (protocolSeparatorIndex === -1) {
    return rawUrl.replace(/\/{2,}/g, '/');
  }

  const protocol = rawUrl.slice(0, protocolSeparatorIndex);
  const rest = rawUrl.slice(protocolSeparatorIndex + 3);

  return `${protocol}://${rest.replace(/\/{2,}/g, '/')}`;
};

export const getAccessToken = () => {
  const state = store.getState();

  if (!state || !state.auth || !state.auth.userdata) {
    return null;
  }

  const userAuth = state.auth.userdata.userAuth;

  if (!userAuth || !userAuth.token) {
    return null;
  }

  return userAuth.token.access_token || null;
};

export const buildAuthHeaders = (additionalHeaders = {}) => {
  const baseHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  const accessToken = getAccessToken();

  if (!accessToken) {
    return baseHeaders;
  }

  return Object.assign({}, baseHeaders, {
    Authorization: `Bearer ${accessToken}`
  });
};

// 呼び出しもとはエラーを独自処理しない限り, response.status === ResponseStatus.SUCCESS で成功時の挙動を制限すること
// エラーを独自処理したい場合、showGeneralPopup, showGeneralPopupIfValidateFail, showInternalServerError を false にすること
export const apiRequest = async ({
  url,
  method = 'GET',
  data,
  params,
  headers = {},
  baseURL,
  timeout = 30000,
  showGeneralPopup = true,
  useLoading = false,
  showGeneralPopupIfValidateFail = true,
  showInternalServerError = true
}) => {
  const upperMethod = method.toUpperCase();
  const requestHeaders = buildAuthHeaders(headers);
  const accessToken = getAccessToken();

  if (useLoading) {
    store.dispatch(showLoading());
  }

  const normalizedUrl = normalizeUrlSlashes(url);

  try {
    const response = await axios.request({
      url: normalizedUrl,
      method: upperMethod,
      baseURL,
      headers: requestHeaders,
      data: upperMethod === 'GET' ? undefined : data,
      params,
      timeout
    });

    return response;
  } catch (error) {
    const response = error && error.response;
    const responseData = response && response.data;

    if (!response) {
      throw error;
    }

    if (response.status === ResponseStatus.DUPLICATE_USER) {
      return response.data;
    }

    if (response.status === 503) {
      store.dispatch(showMaintain());
      return error;
    }

    if (response.status === 401) {
      store.dispatch(clearPersistRoot());
      store.dispatch(initializeReducers());
      localStorage.setItem(
        'redirect_to',
        window.location.pathname.startsWith('/login')
          ? ''
          : window.location.pathname + (window.location.search || '')
      );
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
      if (accessToken) {
        const action = {
          title: responseData && responseData.message
        };
        store.dispatch(showErrorPopup(action));
      }
      return response;
    }

    if (checkActiveSubscriptionPause(response)) {
      const pauseErrors = responseData && responseData.errors;
      if (pauseErrors) {
        const action = {
          title: pauseErrors.title,
          type: pauseErrors.type,
          message: pauseErrors.messages
        };
        store.dispatch(showErrorPopup(action));
      }
      return response;
    }

    if (response.status === ResponseStatus.VALIDATEFAIL) {
      if (showGeneralPopupIfValidateFail) {
        const responseErrors = responseData && responseData.errors;
        let messages = (responseErrors && responseErrors.messages) || [];
        if (typeof messages === 'object' && !Array.isArray(messages)) {
          messages = Object.values(messages);
        }
        const action = {
          title: responseErrors && responseErrors.title,
          type: (responseErrors && responseErrors.type) || '',
          message: messages
        };
        store.dispatch(showErrorPopup(action));
        return response;
      } else {
        throw error;
      }
    }

    if (response.status === ResponseStatus.INTERNAL_SERVER_ERROR) {
      if (showInternalServerError) {
        const action = {
          title: responseData && responseData.message
        };
        store.dispatch(showErrorPopup(action));
        return response;
      } else {
        throw error;
      }
    }

    if (response.status > 401) {
      if (showGeneralPopup) {
        const res = responseData;
        if (res && res.errors) {
          const { type, messages, title, nextUrl } = res.errors;
          const action = {
            type,
            title,
            nextUrl,
            message: messages && Object.values(messages)
          };
          store.dispatch(showErrorPopup(action));
          return response;
        } else {
          const message = res && res.message;
          if (typeof message === 'object' && message !== null) {
            const flatMessages = Object.values(message).flat();
            const action = {
              title: flatMessages.join(' ')
            };
            store.dispatch(showErrorPopup(action));
          } else if (message) {
            const action = {
              title: message
            };
            store.dispatch(showErrorPopup(action));
          }
        }
      } else {
        throw error;
      }
    }

    return response;
  } finally {
    if (useLoading) {
      store.dispatch(hideLoading());
    }
  }
};
